import { CalendarOutlined, CopyOutlined, EditOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import useOptions from "features/masterData/hooks/useOptions";
import { generateRandomString } from "utils/helper";
import * as service from "../services/holidayApi";

import { Dropdown, Menu, Modal } from "antd";
import HolidayCalendarTable from "components/table/HolidayCalendar";
import HolidayCalendarModal from "../components/HolidayCalendarModal";
import HolidayEmployeeCalendarModal from "../components/HolidayEmployeeCalendarModal";
import HolidayModal from "../components/HolidayModal";
import { HOLIDAY_PAGE } from "../schemas/holidaySchema";
import {
  setCurrentCalendar,
  setEmployeeByCompany,
  setEmployeeList,
  setHolidayCalendar,
  setHolidayGroup,
  setIsLoading
} from "../slices/holidaySlice";
import EmployeeHolidayTable from "../table/EmployeeHolidayTable";
import "./useHolidayCalendar.css";

function toInitialValues(initialValues = {}) {
  const { holidayType = {}, ...rest } = initialValues;
  const modifiedValues = {
    ...rest,
    holidayType: holidayType.holidayTypeId,
  };
  return { ...modifiedValues };
}

function useHolidayCalendar() {
  const dispatch = useDispatch();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const [holidayCalendarGroupId, setHolidayCalendarGroupId] = useState('');
  const { roles } = useSelector((state) => state.company);
  // * dataSource
  const calendarList = useSelector((state) => state.holidayCalendar.holidayGroup);
  const calendarDetails = useSelector((state) => state.holidayCalendar.currentCalendar);
  const holidayList = useSelector((state) => state.holidayCalendar.holidayCalendar);
  const employeeList = useSelector((state) => state.holidayCalendar.employeeList);
  const employeeByCompanyId = useSelector((state) => state.holidayCalendar.employeeByCompany);
  // * state
  const isLoading = useSelector((state) => state.holidayCalendar.isLoading);
  const [calendarState, setCalendarState] = useState({});
  const [holidayDetails, setHolidayDetails] = useState({});
  const [pageType, setPageType] = useState("holiday")
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedYear, setYear] = useState(new Date().getFullYear() + "");
  const { holidayTypeOptions = [], getHolidayTypeOptions } = useOptions();
  const [action, setAction] = useState("calendar")
  const { t, i18n } = useTranslation();
  const modified = {}
  const initialValues = useMemo(() => {
    return toInitialValues(holidayList);
  }, [holidayList]);

  const getCalendarList = useCallback(async () => {
    try {

      dispatch(setIsLoading(true));
      const response = await service.getHolidayCalendarGroupList({
        companyId: currentCompany,
        employeeId: currentEmployee,
        requestId: generateRandomString()
      })
      dispatch(setHolidayGroup(response?.data))

    } catch (error) {
      alert({ type: "error", resultObject: error })
    } finally {
      dispatch(setIsLoading(false))
    }
  }, [currentCompany, currentEmployee, calendarList])

  const toInitialValuesDay = (initialValues = {}) => {
    return {
      ...initialValues,
      calendarName: initialValues?.calendarName,
    };
  };

  const initialValuesDay = useMemo(() => {
    return toInitialValuesDay(calendarState);
  }, [currentCompany, calendarState]);

  const getCalendarById = useCallback(async (values) => {
    try {
      dispatch(setIsLoading(true));
      const response = await service.getCalendarById({
        companyId: currentCompany,
        actionId: currentEmployee,
        holidayCalendarGroupId: values,
      })
      dispatch(setCurrentCalendar(response?.data))

    } catch (error) {
      alert({ type: "error", resultObject: error })
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [
    currentCompany,
    currentEmployee,
    calendarDetails
  ])

  const calendarManagement = useCallback(async (values) => {
    try {

      dispatch(setIsLoading(true))

      await service.holidayGroupManagement({
        calendarName: values?.calendarName,
        calendarNameEn: values?.calendarNameEn,
        companyId: currentCompany,
        actionId: currentEmployee,
        holidayCalendarGroupId: values?.holidayCalendarGroupId
      });
      setIsModalOpen(false)
      getCalendarList();
    } catch (error) {
      alert({ type: "error", resultObject: error });
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [])


  const getHolidayPagingByCalendar = useCallback(async (value) => {
    try {
      dispatch(setIsLoading(true))
      const response = await service.getHolidayPagingByCalendar({
        companyId: currentCompany,
        employeeId: currentEmployee,
        actionId: currentEmployee,
        holidayCalendarGroupId: calendarDetails?.holidayCalendarGroupId,
        year: selectedYear,
        ...value,
        page: 0,
        size: 50,
        sortBy: [
          {
            "direction": "ASC",
            "property": "id"
          }
        ]
      })
      dispatch(setHolidayCalendar(response?.data?.page?.content))
    } catch (error) {
      alert({ type: "error", resultObject: error })
    } finally {
      dispatch(setIsLoading(false))
    }
  }, [
    currentCompany,
    currentEmployee,
    selectedYear,
    calendarDetails,

  ])

  const holidayManagement = useCallback(async (values) => {
    try {

      dispatch(setIsLoading(true))
      await service.holidayManagement({
        ...values,
        date: values?.date.format("YYYY-MM-DD"),
        holidayCalendarGroupId: values?.holidayCalendarGroupId,
        companyId: currentCompany,
        actionId: currentEmployee
      })
      setIsModalOpen(false)
      getHolidayPagingByCalendar()
      getPage()
    } catch (error) {
      alert({ type: "error", resultObject: error })
    } finally {
      dispatch(setIsLoading(false))
    }
  }, [currentCompany, currentEmployee, calendarDetails])



  const getEmployeeCalendarPaging = useCallback(async () => {
    try {
      dispatch(setIsLoading(true))
      const response = await service.getEmployeeCalendarPaging({
        companyId: currentCompany,
        employeeId: currentEmployee,
        actionId: currentEmployee,
        holidayCalendarGroupId: calendarDetails?.holidayCalendarGroupId,
        year: selectedYear,
        page: 0,
        size: 50,
        sortBy: [
          {
            "direction": "ASC",
            "property": "id"
          }
        ]
      })
      dispatch(setEmployeeList(response?.data?.content))
    } catch (error) {
      alert({ type: "error", resultObject: error })
    } finally {
      dispatch(setIsLoading(false))
    }
  }, [currentCompany, currentEmployee, calendarDetails])


  const onStatus = useCallback(
    async (status, holidayId) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to change status ?</span>
          : <span>คุณยืนยันการเปลี่ยนสถานะใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true))
            await service.manageStatusHolidayCalendar({
              companyId: currentCompany,
              employeeId: currentEmployee,
              actionId: currentEmployee,
              holidayCalendarGroupId: calendarDetails?.holidayCalendarGroupId,
              holidayCalendarId: holidayId,
              statusFlag: status ? "ACTIVE" : "INACTIVE",
            })
            getHolidayPagingByCalendar()
            getPage()
          } catch (error) {
            alert({ type: "error", resultObject: error })
          } finally {
            dispatch(setIsLoading(false))
          }
        }
      })
    }, [currentCompany, calendarDetails, dispatch])

  const onDuplicateCalendar = useCallback(async () => {
    try {
      dispatch(setIsLoading(true))
      await service.duplicateCalendar({
        companyId: currentCompany,
        employeeId: currentEmployee,
        actionId: currentEmployee,
        holidayCalendarGroupId: calendarDetails?.holidayCalendarGroupId,
      })
      getCalendarList()
    } catch (error) {
      alert({ type: "error", resultObject: error })
    } finally {
      dispatch(setIsLoading(false))
    }
  }, [currentCompany,
    currentEmployee,
    currentEmployee,
    calendarDetails])

  const onPageChange = useCallback((page) => {
    setPageType(page)
  }, [pageType])



  const handleOnPageChange = useCallback(() => {
    switch (pageType) {
      case HOLIDAY_PAGE.HOLIDAY:
        getHolidayPagingByCalendar()
        break

      case HOLIDAY_PAGE.EMPLOYEE:
        getEmployeeCalendarPaging()
        break

      default:
        return <div />;
    }
    getPage()
  }, [currentCompany, currentEmployee, pageType, calendarDetails])



  const handleOnCalendarChange = useCallback((values) => {
    getCalendarById(values);
    setHolidayCalendarGroupId(values);
    console.log('holidayCalendarGroupId', holidayCalendarGroupId);

  }, [currentCompany, currentEmployee])



  const employeeCalendarManagement = useCallback(async (values) => {
    try {

      dispatch(setIsLoading(true))
      const requestBody = {
        holidayCalendarGroupId: values?.holidayCalendarGroupId,
        employeeListId: values?.employeeListId,
        companyId: currentCompany,
        actionId: currentEmployee
      }
      await service.employeeHolidayCalendarManagement(requestBody)
      setIsModalOpen(false)
      getEmployeeCalendarPaging()
      getPage()

    } catch (error) {
      alert({ type: "error", resultObject: error })
    } finally {
      dispatch(setIsLoading(false))
    }
  }, [currentCompany, currentEmployee, calendarDetails])

  const onEdit = useCallback((values) => {
    setIsModalOpen(true)
    setCalendarState(values)
    setCurrentCalendar(values)
    setAction("calendar")
  }, [currentCompany])

  const onHolidayEdit = useCallback((values) => {
    setIsModalOpen(true)
    setHolidayDetails(values)
    setAction("holiday")

  }, [currentCompany])

  const onEmployeeEdit = useCallback(() => {
    setIsModalOpen(true)
    setAction("employee")
    getAllEmployee()
  }, [currentCompany, currentEmployee])

  const getAllEmployee = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await service.getEmployeeByCompanyId({
        companyId: currentCompany,
        actionId: currentEmployee,
        employeeId: currentEmployee
      })
      dispatch(setEmployeeByCompany(response?.data))
    } catch (error) {
      alert({ type: "error", resultObject: error })
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, currentEmployee])


  const onCancel = useCallback(() => {
    setIsModalOpen(false)
    setAction("calendar")
    setCurrentCalendar({})
    setHolidayDetails({})
  })

  const onSearch = useCallback((values) => {

    values.page = 0
    values.size = 50
    getHolidayPagingByCalendar(values);
  }, [currentCompany, currentEmployee, calendarDetails])

  const handleOnYearChange = useCallback((dateString) => {
    setYear(dateString);
    const modifiedParams = {
      companyId: currentCompany,
      employeeId: currentEmployee,
      actionId: currentEmployee,
      year: dateString,
    };
    getHolidayPagingByCalendar(modifiedParams);
  }, [currentCompany, currentEmployee]);

  const calendarTabs = useCallback(() => {
    return calendarList?.map(group => {
      const menu = (
        <Menu>
          {roles.includes('UPDATE_CONFIGURATION|ATTENDANCE_SETTING|HOLIDAY_CALENDAR') && (<Menu.Item
            key="1"
            icon={<EditOutlined style={{ fontSize: '18px', color: 'gray' }} />}
            onClick={() => onEdit(group)}
          >
            <span style={{ color: 'grey', fontSize: '14px' }}>{t("action.edit")}</span>
          </Menu.Item>)}
          <Menu.Item
            key="2"
            icon={<CopyOutlined rotate={180} style={{ fontSize: '18px', color: 'gray' }} />}
            onClick={() => onDuplicateCalendar()}
          >
            <span style={{ color: 'grey', fontSize: '14px' }}>{t("common.copy")}</span>
          </Menu.Item>
        </Menu>
      );

      return {
        label: (
          <span>
            <CalendarOutlined />
            {i18n.language === "en" ? group?.calendarNameEn : group?.calendarName}
            <Dropdown overlay={menu} trigger={['click']}>
              <a className="ant-dropdown-link" onClick={e => e.preventDefault()} style={{ marginLeft: 8 }}>
                <span style={{ fontSize: '16px', color: 'gray' }}>⋮</span>
              </a>
            </Dropdown>
          </span>
        ),

        key: group?.holidayCalendarGroupId,
        tab: group,


      };
    }) || [];
  }, [currentCompany, currentEmployee, calendarList]);

  const getCalendarModal = useCallback(() => {
    switch (pageType) {
      case HOLIDAY_PAGE.HOLIDAY:
        return action === "holiday" ?
          <HolidayModal
            isModalOpen={isModalOpen}
            holidayDetails={holidayDetails}
            onCancel={onCancel}
            handleOk={onHolidayEdit}
            holidayTypeOptions={holidayTypeOptions}
            calendarList={calendarList}
            handleOnHolidaySubmit={holidayManagement}
            currentCalendar={calendarDetails}

          />
          : <HolidayCalendarModal
            title={t("common.edit")}
            isModalOpen={isModalOpen}
            onCancel={onCancel}
            handleOk={onEdit}
            currentCalendar={initialValuesDay}
            handleOnCalendarSubmit={calendarManagement}
          />
      case HOLIDAY_PAGE.EMPLOYEE:
        return <HolidayEmployeeCalendarModal title={t("common.edit")}
          isModalOpen={isModalOpen}
          onCancel={onCancel}
          handleOk={onEdit}
          currentCalendar={calendarDetails}
          employeeCalendarManagement={employeeCalendarManagement}
          employeeList={employeeByCompanyId}
        />
      default:
        return <div />;
    }
  }, [isModalOpen])

  const getModal = useCallback(() => {
    switch (action) {
      case "holiday":
        return <HolidayModal
          isModalOpen={isModalOpen}
          holidayDetails={holidayDetails}
          onCancel={onCancel}
          handleOk={onHolidayEdit}
          holidayTypeOptions={holidayTypeOptions}
          calendarList={calendarList}
          handleOnHolidaySubmit={holidayManagement}
          currentCalendar={calendarDetails}
        />

      case "calendar":
        return <HolidayCalendarModal
          title={t("common.edit")}
          isModalOpen={isModalOpen}
          onCancel={onCancel}
          handleOk={onEdit}
          currentCalendar={initialValuesDay}
          handleOnCalendarSubmit={calendarManagement}
        />
      case "employee":
        return <HolidayEmployeeCalendarModal title={t("common.edit")}
          isModalOpen={isModalOpen}
          onCancel={onCancel}
          handleOk={onEmployeeEdit}
          currentCalendar={calendarDetails}
          employeeCalendarManagement={employeeCalendarManagement}
          employeeList={employeeByCompanyId}
        />
      default:
        return <div />;
    }
  }, [isModalOpen])
  // * event controller
  const getPage = useCallback(() => {
    switch (pageType) {

      case HOLIDAY_PAGE.HOLIDAY:
        return <HolidayCalendarTable
          dataSource={holidayList}
          onStatus={onStatus}
          onEdit={onHolidayEdit}
          pagination={false}
        />;

      case HOLIDAY_PAGE.EMPLOYEE:

        return <EmployeeHolidayTable
          isLoading={isLoading}
          dataSource={employeeList} />;

      default:
        return <div />;
    }
  }, [pageType, holidayList, employeeList, isLoading, calendarDetails])


  useEffect(() => {
    getHolidayTypeOptions();
  }, [getHolidayTypeOptions]);

  useEffect(() => {
    getAllEmployee();
  }, [getAllEmployee])

  useEffect(() => {
    if (currentCompany) {
      getCalendarList();
    }
  }, [currentCompany, currentEmployee]);

  useEffect(() => {
    if (calendarList?.length > 0) {
      getCalendarById(calendarList?.[0]?.holidayCalendarGroupId)
    }
  }, [calendarList, currentCompany, currentEmployee])

  useEffect(() => {
    if (calendarList?.length > 0) {
      handleOnPageChange()
    }
  }, [getCalendarById, currentCompany,
    currentEmployee,
    pageType])

  return {
    initialValues,
    isLoading,
    calendarList,
    holidayList,
    pageType,
    initialValuesDay,
    holidayCalendarGroupId,
    handleOnCalendarChange,
    handleOnPageChange: onPageChange,
    onCancel,
    onYearChange: handleOnYearChange,
    getPage,
    getCalendarModal: getModal,
    calendarTabs,
    onEmployeeEdit,
    onHolidayEdit,
    onEdit,
    onSearch,
    holidayTypeOptions,
  };
}

export default useHolidayCalendar;
